import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";

import Layout from '../components/Layout';
import { Col, Row, Container, Form, Button, Card, Alert, Spinner } from '@themesberg/react-bootstrap';
import { BASE_URL } from '../constants';
// ChangeRoom component definition
const ChangeRoom = () => {
  const [shelters, setShelters] = useState([]);
  const [rooms, setRooms] = useState([]); // Store rooms available for selection
  const [shelter, setShelter] = useState('');
  const [room, setRoom] = useState('');
  const [reason, setReason] = useState('');
  const [changeType, setChangeType] = useState('');
  const [currentShelter, setCurrentShelter] = useState(null);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [userId, setUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [warningMessage, setWarningMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [alertType, setAlertType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
 

  // Fetch user data and set userId (from localStorage)
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData')) || {};
    const refugeeId = userData?.id;
    setUserId(refugeeId);
  }, []);

  // Fetch shelter data when component mounts
  useEffect(() => {
    const fetchShelters = async () => {
      try {
        const response = await fetch(`${BASE_URL}/shelters`);
        const data = await response.json();
        setShelters(data);
      } catch (error) {
        console.error('Error fetching shelters:', error);
      }
    };
    fetchShelters();
  }, []);

  useEffect(() => {
    if (userId) {
      const fetchCurrentShelter = async () => {
        try {
          const response = await fetch(`${BASE_URL}/shelters/occupant/${userId}`);
          const data = await response.json();
          if (data) {
            setCurrentShelter(data.shelter);
            const currentRoom = data.rooms.withOccupant.find((room) => room.occupant.includes(userId));
            const otherRooms = data.rooms.others; // Get available rooms
            setCurrentRoom(currentRoom);
            setRooms(otherRooms);
          }
        } catch (error) {
          console.error('Error fetching current shelter:', error);
        }
      };
      fetchCurrentShelter();
    }
  }, [userId]);

  // Handle shelter change selection
  const handleShelterChange = (e) => {
    const selectedShelterId = e.target.value;
    setShelter(selectedShelterId);

    const selectedShelter = shelters.find(s => s._id === selectedShelterId) || currentShelter;
    if (selectedShelter) {
      setRooms(selectedShelter.rooms);
    }
    setRoom('');
  };

  // Handle change type selection (room or shelter)
  const handleChangeType = (e) => {
    setChangeType(e.target.value);
    setShelter('');
    setRoom('');
    
  };

  const handleSave = async () => {
    setIsLoading(true);
   setSuccessMessage(null);
   setWarningMessage(null);
   setErrorMessage(null);

    const requestBody = {
      userId,
      currentShelterId: currentShelter?.id,
      requestedShelterId: shelter || currentShelter?.id,
      currentRoomId: currentRoom?._id,
      roomId: rooms.find(r => r.roomNumber === room)?._id,
      reason,
    };

    try {
      const response = await fetch(`${BASE_URL}/shelters/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (!response.ok) {
        // Check for the pending change request error
        if (data.message === 'You already have a pending change request') {
          setWarningMessage("You already have a pending change request.");
          setAlertType('warning');
        } else if (data.message === 'All fields are required') {
          setErrorMessage("All fields are required.");
        } else {
          console.error('Error requesting change:', data);
        }
      } else {
        setShelter("");
        setRoom("");
        setReason("");
        setChangeType("");
        setSuccessMessage("Change request sent successfully!");
      }
    } catch (error) {
      console.error('Error requesting change:', error);
    } finally {
      setIsLoading(false); 
      setTimeout(() => {
        setSuccessMessage(null);
        setWarningMessage(null);
      }, 5000);
  };
  }
  

  return (
    <Layout>
              <Container className="py-4 px-0"> 
        <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-7">
                <Card className="shadow-lg border-0 rounded">
                    <Card.Body className="p-6">
              <h4 className="mb-4 text-center text-primary">Change Room or Shelter</h4>
              
              {/* Current Shelter and Room */}
              <Row className="mb-4">
                <Col md={8}>
                  <div>
                    <h5 className="text-muted">Current Shelter</h5>
                    <p><strong>{currentShelter?.name || 'Not Available'}</strong></p>
                  </div>
                  <div>
                    <h5 className="text-muted">Current Room</h5>
                    <p><strong>{currentRoom?.roomNumber || 'Not Available'}</strong></p>
                  </div>
                </Col>
              </Row>
              {currentShelter && (
              <Form>
                {/* Change Type */}
                <Form.Group controlId="changeType" className="mt-4">
                  <Form.Label>Select Change Type</Form.Label>
                  <Form.Control as="select" value={changeType} onChange={handleChangeType}>
                    <option value="">Select Change Type</option>
                    <option value="room">Move to another room in the current shelter</option>
                    <option value="shelter">Move to a different shelter</option>
                  </Form.Control>
                </Form.Group>
  
                {/* Room Change Options */}
                {changeType === 'room' && currentShelter && rooms.length > 0 && (
                  <Form.Group controlId="newRoom" className="mt-3">
                    <Form.Label>Select Room in Current Shelter</Form.Label>
                    <Form.Control
                      as="select"
                      value={room}
                      onChange={(e) => setRoom(e.target.value)}
                      disabled={rooms.length === 0}
                    >
                      <option value="">Select Room</option>
                      {rooms.map((room) => (
                        <option key={room._id} value={room.roomNumber}>
                          {room.roomNumber}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
  
                {/* Shelter Change Options */}
                {changeType === 'shelter' && (
                  <Form.Group controlId="newShelter" className="mt-4">
                    <Form.Label>Select New Shelter</Form.Label>
                    <Form.Control as="select" value={shelter} onChange={handleShelterChange}>
                      <option value="">Select Shelter</option>
                      {shelters.map((shelter) => (
                        <option key={shelter._id} value={shelter._id}>
                          {shelter.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
  
                {changeType === 'shelter' && shelter && rooms.length > 0 && (
                  <Form.Group controlId="newRoom" className="mt-3">
                    <Form.Label>Select Room in New Shelter</Form.Label>
                    <Form.Control
                      as="select"
                      value={room}
                      onChange={(e) => setRoom(e.target.value)}
                      disabled={rooms.length === 0}
                    >
                      <option value="">Select Room</option>
                      {rooms.map((room) => (
                        <option key={room._id} value={room.roomNumber}>
                          {room.roomNumber}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
  
                {/* Reason for Change */}
                <Form.Group controlId="reason" className="mt-3">
                  <Form.Label>Reason for Change</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter your reason here..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </Form.Group>
                {successMessage && (
  <Alert variant="success" className="mt-3">
    {successMessage}
  </Alert>
)}

{warningMessage && (
  <Alert variant="warning" className="mt-3">
    {warningMessage}
  </Alert>
)}
{errorMessage && (
  <Alert variant="danger" className="mt-3">
    {errorMessage}
  </Alert>
)}


                <Button
        variant="primary"
        className="mt-4 w-100"
        onClick={handleSave}
        disabled={isLoading} // Disable button while loading
      >
        {isLoading ? (
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
        ) : (
          'Submit Request'
        )}
      </Button>
              </Form>
              )}
              </Card.Body>
                </Card>
            </div>
        </div>
        </ Container>
      </ Layout>
  
  );
  
};

export default ChangeRoom;
